import { NumberInput, Select, SimpleGrid, Stack, Switch, Text, Textarea, TextInput } from "@mantine/core";
import type { ChainInputField } from "@passio/easyai-client";

export type ChainFormValues = Record<string, string | number | boolean>;

export const initialValues = (fields: ChainInputField[]): ChainFormValues =>
  Object.fromEntries(fields.map((f) => [f.key, f.default ?? (f.type === "boolean" ? false : "")]));

/** input_data for the run: blanks dropped so the chain applies its own defaults. */
export function toInputData(fields: ChainInputField[], values: ChainFormValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    const v = values[f.key];
    if (v === "" || v === undefined) continue;
    out[f.key] = typeof v === "string" ? v.trim() : v;
  }
  return out;
}

export const missingRequired = (fields: ChainInputField[], values: ChainFormValues) => fields.filter((f) => f.required && (values[f.key] === "" || values[f.key] === undefined || (typeof values[f.key] === "string" && !(values[f.key] as string).trim())));

const below = { inputWrapperOrder: ["label", "input", "description", "error"] as ("label" | "input" | "description" | "error")[] };

function NumberField({ f, value, onChange, disabled }: { f: ChainInputField; value: string | number | boolean | undefined; onChange: (v: string | number) => void; disabled?: boolean }) {
  if (f.autoLabel) {
    const lo = f.min ?? 1;
    const hi = f.max ?? 14;
    const data = [{ value: "0", label: f.autoLabel }, ...Array.from({ length: hi - lo + 1 }, (_, i) => ({ value: String(lo + i), label: String(lo + i) }))];
    return <Select label={f.label} description={f.help} data={data} value={String(Number(value) || 0)} onChange={(v) => onChange(Number(v ?? 0))} allowDeselect={false} disabled={disabled} {...below} />;
  }
  return (
    <NumberInput
      label={f.label}
      description={f.help}
      placeholder={f.placeholder}
      min={f.min}
      max={f.max}
      allowDecimal={false}
      value={value === "" || value === undefined ? "" : Number(value)}
      onChange={(v) => onChange(v === "" ? "" : Number(v))}
      disabled={disabled}
      {...below}
    />
  );
}

/** Form generated from a chain's runtime inputs: the request and examples, then one row of options. */
export function ChainForm({ fields, values, onChange, examples, disabled }: { fields: ChainInputField[]; values: ChainFormValues; onChange: (v: ChainFormValues) => void; examples?: string[]; disabled?: boolean }) {
  const set = (k: string, v: string | number | boolean) => onChange({ ...values, [k]: v });
  const text = fields.filter((f) => f.type === "textarea" || f.type === "text");
  const options = fields.filter((f) => f.type === "number" || f.type === "select");
  const toggles = fields.filter((f) => f.type === "boolean");
  const exampleTarget = text.find((f) => f.type === "textarea");

  return (
    <Stack gap="md">
      {text.map((f) =>
        f.type === "textarea" ? (
          <Stack key={f.key} gap="xs">
            <Textarea label={f.label} description={f.help} placeholder={f.placeholder} required={f.required} autosize minRows={4} maxRows={12} value={String(values[f.key] ?? "")} onChange={(e) => set(f.key, e.currentTarget.value)} disabled={disabled} />
            {f === exampleTarget && examples && examples.length > 0 && (
              <div>
                <Text fz="xs" fw={650} c="dimmed" mb={6}>
                  Or start from an example
                </Text>
                <SimpleGrid cols={{ base: 1, sm: Math.min(3, examples.length) }} spacing="xs">
                  {examples.map((ex) => (
                    <button key={ex} type="button" className="pd-example" onClick={() => set(f.key, ex)} disabled={disabled} title={ex}>
                      {ex}
                    </button>
                  ))}
                </SimpleGrid>
              </div>
            )}
          </Stack>
        ) : (
          <TextInput key={f.key} label={f.label} description={f.help} placeholder={f.placeholder} required={f.required} value={String(values[f.key] ?? "")} onChange={(e) => set(f.key, e.currentTarget.value)} disabled={disabled} />
        ),
      )}
      {(options.length > 0 || toggles.length > 0) && (
        <div className="pd-options">
          {options.map((f) =>
            f.type === "select" ? (
              <Select key={f.key} label={f.label} description={f.help} data={f.options ?? []} value={String(values[f.key] ?? "")} onChange={(v) => set(f.key, v ?? "")} allowDeselect={false} disabled={disabled} {...below} />
            ) : (
              <NumberField key={f.key} f={f} value={values[f.key]} onChange={(v) => set(f.key, v)} disabled={disabled} />
            ),
          )}
          {toggles.length > 0 && (
            <Stack gap={10} pb={6}>
              {toggles.map((f) => (
                <Switch key={f.key} label={f.label} description={f.help} checked={Boolean(values[f.key])} onChange={(e) => set(f.key, e.currentTarget.checked)} disabled={disabled} />
              ))}
            </Stack>
          )}
        </div>
      )}
    </Stack>
  );
}
